import { FamilySharedEmail } from '../components/FamilySharedEmail';

import { stableRecordKey } from '../lib/rowIdentity';
import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Loader2, CheckCircle2, AlertCircle, Pencil, IdCard, Clock, CreditCard, MapPin, Users, ArrowRight, ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DocumentUpload } from '../components/DocumentUpload';
export const FIELD_LABELS = {
  full_name: 'Full name (as shown on your photo ID)',
  email: 'Email address',
  family_shared_email: 'Family shared email acknowledgement',
  phone: 'Phone number',
  dob: 'Date of birth (optional)',
  gender: 'Gender',
  address_line_1: 'House number and street',
  address_line_2: 'Area / locality',
  city: 'Town or city',
  county: 'County',
  postcode: 'Postcode'
};
export const GENDER_LABELS = {
  male: 'Male',
  female: 'Female',
  other: 'Other',
  prefer_not_to_say: 'Prefer not to say'
};
export const fmtDob = iso => {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
};
export const Notice = ({
  tone = 'info',
  children,
  testId
}) => <div data-testid={testId} className={`rounded-xl px-4 py-3 text-base flex gap-3 items-start ${tone === 'warn' ? 'bg-amber-50 border border-amber-200 text-amber-900' : tone === 'good' ? 'bg-emerald-50 border border-emerald-200 text-emerald-900' : 'bg-[hsl(var(--lux-royal-soft))] border border-[hsl(var(--lux-royal)/0.15)] text-[hsl(var(--lux-ink))]'}`}>
    {tone === 'warn' ? <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" /> : <ShieldCheck className="w-5 h-5 mt-0.5 shrink-0" />}
    <div className="min-w-0">{children}</div>
  </div>;

// Phone number keypads often have no "/" key: accept digits and insert the slashes automatically (DD/MM/YYYY).
export
// Phone number keypads often have no "/" key: accept digits and insert the slashes automatically (DD/MM/YYYY).
const maskDob = raw => {
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw || '');
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const d = (raw || '').replace(/\D/g, '').slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
};
export const INPUT_ATTRS = {
  email: {
    inputMode: 'email',
    autoCapitalize: 'none',
    autoCorrect: 'off',
    spellCheck: false,
    autoComplete: 'email'
  },
  phone: {
    inputMode: 'tel',
    autoComplete: 'tel'
  },
  postcode: {
    autoCapitalize: 'characters',
    autoCorrect: 'off',
    spellCheck: false,
    autoComplete: 'postal-code'
  },
  dob: {
    inputMode: 'numeric',
    autoComplete: 'bday'
  },
  full_name: {
    autoCapitalize: 'words',
    autoComplete: 'name'
  },
  address_line_1: {
    autoComplete: 'address-line1'
  },
  address_line_2: {
    autoComplete: 'address-line2'
  },
  city: {
    autoComplete: 'address-level2'
  },
  county: {
    autoComplete: 'address-level1'
  }
};
export function ClaimField({
  name,
  type = 'text',
  placeholder,
  hint,
  value,
  missing,
  onChange
}) {
  return <div className="space-y-1.5">
      <Label htmlFor={`claim-${name}`} className="text-base text-[hsl(var(--lux-ink))]">{FIELD_LABELS[name]}{missing && <span className="ml-2 text-sm font-semibold text-amber-700" data-testid={`claim-missing-${name}`}>Required — please complete</span>}</Label>
      <Input id={`claim-${name}`} data-testid={`claim-field-${name}`} type={type} value={name === 'dob' ? maskDob(value) : value} placeholder={placeholder} {...INPUT_ATTRS[name] || {}} onChange={e => onChange(name, name === 'postcode' ? e.target.value.toUpperCase() : name === 'dob' ? maskDob(e.target.value) : e.target.value)} className={`h-12 text-base ${missing ? 'border-amber-400 bg-amber-50/40' : ''}`} />
      {hint && <p className="text-sm text-[hsl(var(--lux-ink)/0.6)]">{hint}</p>}
    </div>;
}
export const ClaimStep6 = ({
  member
}) => <section className="space-y-5 text-center py-4" data-testid="claim-step-6">
          <Clock className="w-14 h-14 mx-auto text-[hsl(var(--lux-gold))]" />
          <h2 className="text-2xl font-bold text-[hsl(var(--lux-royal))]" style={{
    fontFamily: 'Cormorant Garamond'
  }}>Thank you, {member.first_name} — your membership is being verified</h2>
          <p className="text-base text-[hsl(var(--lux-ink))]">Our team will review your details and photo ID. Once verified, your new digital membership card will be created and emailed to you. There is nothing more you need to do for now.</p>
          <p className="text-sm text-[hsl(var(--lux-ink)/0.6)]">Your membership number stays exactly the same: <strong>{member.membership_number}</strong>.</p>
          <Link to="/"><Button variant="outline" className="h-12 text-base">Back to home</Button></Link>
        </section>;
export const ClaimStep1 = ({
  form,
  isMissing,
  missing,
  setStep
}) => <section className="space-y-5" data-testid="claim-step-1">
          <h2 className="text-2xl font-bold text-[hsl(var(--lux-royal))]" style={{
    fontFamily: 'Cormorant Garamond'
  }}>Step 1 — Please review your details</h2>
          <p className="text-base text-[hsl(var(--lux-ink))]">Please review the information we currently hold and update anything that has changed.</p>
          <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-base">
            {Object.keys(FIELD_LABELS).map(k => <div key={k} className="border-b border-[hsl(var(--lux-royal)/0.08)] pb-2">
                <dt className="text-xs uppercase tracking-wider text-[hsl(var(--lux-ink)/0.55)]">{FIELD_LABELS[k].split(' (')[0]}</dt>
                <dd className={`mt-0.5 ${isMissing(k) ? 'text-amber-700 font-semibold' : 'text-[hsl(var(--lux-ink))]'}`} data-testid={`claim-review-${k}`}>{k === 'dob' ? fmtDob(form[k]) : k === 'gender' ? GENDER_LABELS[form[k]] || '' : form[k] || ''}{isMissing(k) ? 'Required — please complete' : ''}</dd>
              </div>)}
          </dl>
          <div className="grid sm:grid-cols-2 gap-3">
            <Button variant="outline" className="h-12 text-base" onClick={() => setStep(2)} data-testid="claim-step1-edit"><Pencil className="w-4 h-4 mr-2" />I need to update something</Button>
            <Button className="h-12 text-base bg-[hsl(var(--lux-royal))] text-white" disabled={missing.length > 0} onClick={() => setStep(3)} data-testid="claim-step1-correct">Everything is correct <ArrowRight className="w-4 h-4 ml-2" /></Button>
          </div>
          {missing.length > 0 && <p className="text-sm text-amber-800">Some required details are missing — please choose <strong>I need to update something</strong> to complete them.</p>}
        </section>;
export const ClaimStep4 = ({
  busy,
  claimDone,
  member,
  photoFiles,
  s,
  setPhotoFiles,
  setStep,
  stepFor,
  upload
}) => <section className="space-y-5" data-testid="claim-step-4">
          <h2 className="text-2xl font-bold text-[hsl(var(--lux-royal))]" style={{
    fontFamily: 'Cormorant Garamond'
  }}>Step 4 — Your membership photograph</h2>
          <p className="text-base text-[hsl(var(--lux-ink))]">If you can, please upload a clear recent photograph of yourself. This photograph will appear on your digital GS membership card. A plain background, facing the camera, without sunglasses or a hat works best.</p>
          <Notice testId="claim-photo-optional"><p><strong>This step is optional.</strong> Without a photograph your card is issued without a picture; you can add one later and we will re-issue the card.</p></Notice>
          {s.profile_photo_status === 'UPLOADED' && <Notice tone="good" testId="claim-photo-done">Thank you — your photograph has been received. You may replace it below if you wish.</Notice>}
          <DocumentUpload files={photoFiles} onChange={setPhotoFiles} maxFiles={1} maxSizeMb={30} accept="images" label="" testId="claim-photo-upload" />
          <div className="grid sm:grid-cols-2 gap-3">
            <Button variant="outline" className="h-12 text-base" onClick={() => setStep(claimDone ? stepFor(member) : 3)}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
            {photoFiles.length ? <Button className="h-12 text-base bg-[hsl(var(--lux-royal))] text-white" disabled={busy} onClick={() => upload('photo')} data-testid="claim-photo-submit">{busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Send securely</Button> : <Button className="h-12 text-base bg-[hsl(var(--lux-royal))] text-white" onClick={() => setStep(claimDone ? stepFor(member) : 5)} data-testid="claim-photo-next">{s.profile_photo_status === 'UPLOADED' ? 'Continue' : 'Skip for now'} <ArrowRight className="w-4 h-4 ml-2" /></Button>}
          </div>
        </section>;
export const ClaimStep3 = ({
  busy,
  claimDone,
  idFiles,
  member,
  s,
  setIdFiles,
  setStep,
  stepFor,
  upload
}) => <section className="space-y-5" data-testid="claim-step-3">
          <h2 className="text-2xl font-bold text-[hsl(var(--lux-royal))]" style={{
    fontFamily: 'Cormorant Garamond'
  }}>Step 3 — Your photo ID</h2>
          <p className="text-base text-[hsl(var(--lux-ink))]">If you can, please upload one clear photo or scan of a document that shows your name and date of birth — for example a passport, driving licence or national ID card. This is used only to verify your membership identity.</p>
          <Notice testId="claim-id-optional"><p><strong>This step is optional.</strong> If you do not have a document to hand, press <strong>Skip for now</strong> — you can still complete your claim today and add it later when we remind you.</p></Notice>
          {s.photo_id_status === 'UPLOADED' && <Notice tone="good" testId="claim-id-done">Thank you — your photo ID has been received securely. You may upload a clearer copy below if you wish.</Notice>}
          {s.photo_id_status === 'REJECTED' && <Notice tone="warn">Our team has asked for a fresh copy of your photo ID. Please upload it below.</Notice>}
          <DocumentUpload files={idFiles} onChange={setIdFiles} maxFiles={1} maxSizeMb={30} accept="documents+images" label="" testId="claim-id-upload" />
          <div className="grid sm:grid-cols-2 gap-3">
            <Button variant="outline" className="h-12 text-base" onClick={() => setStep(claimDone ? stepFor(member) : 2)}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
            {idFiles.length ? <Button className="h-12 text-base bg-[hsl(var(--lux-royal))] text-white" disabled={busy} onClick={() => upload('id')} data-testid="claim-id-submit">{busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Send securely</Button> : <Button className="h-12 text-base bg-[hsl(var(--lux-royal))] text-white" onClick={() => setStep(claimDone ? stepFor(member) : 4)} data-testid="claim-id-next">{s.photo_id_status === 'UPLOADED' ? 'Continue' : 'Skip for now'} <ArrowRight className="w-4 h-4 ml-2" /></Button>}
          </div>
          <p className="text-sm text-[hsl(var(--lux-ink)/0.55)]">Your document is stored privately, is never shown on your card or QR code, and is seen only by authorised administrators.</p>
        </section>;
export const ClaimStep5 = ({
  busy,
  guardianNeeded,
  guardianOk,
  hasPending,
  member,
  missing,
  readyToSubmit,
  s,
  setStep,
  submit
}) => <section className="space-y-5" data-testid="claim-step-5">
          <h2 className="text-2xl font-bold text-[hsl(var(--lux-royal))]" style={{
    fontFamily: 'Cormorant Garamond'
  }}>Step 5 — Review and submit</h2>
          <ul className="space-y-2 text-base">
            {[['Your details', !missing.length, missing.length ? `Still needed: ${missing.map(m => FIELD_LABELS[m].split(' (')[0].toLowerCase()).join(', ')}` : 'Complete', 2], ['Photo ID', s.photo_id_status === 'UPLOADED' || s.photo_id_status === 'VERIFIED', s.photo_id_status === 'UPLOADED' || s.photo_id_status === 'VERIFIED' ? 'Received' : s.photo_id_status === 'REJECTED' ? 'A fresh copy has been requested' : 'Optional — not uploaded yet (you can add it later)', 3], ['Membership photograph', s.profile_photo_status === 'UPLOADED', s.profile_photo_status === 'UPLOADED' ? 'Received' : 'Optional — not uploaded yet (you can add it later)', 4], ...(guardianNeeded ? [['Parent or guardian', guardianOk, guardianOk ? `${member.guardian.full_name}` : 'Please link a parent or guardian', 2]] : [])].map(([label, ok, text, go]) => <li key={label} className="flex items-center justify-between gap-3 rounded-lg border border-[hsl(var(--lux-royal)/0.1)] bg-white px-4 py-3" data-testid={`claim-check-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <span className="flex items-center gap-2">{ok ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <AlertCircle className="w-5 h-5 text-amber-600" />}<span className="font-medium">{label}</span><span className="text-[hsl(var(--lux-ink)/0.6)]">— {text}</span></span>
                {!ok && <Button size="sm" variant="outline" onClick={() => setStep(go)}>{go >= 3 ? 'Add' : 'Fix'}</Button>}
              </li>)}
          </ul>
          {hasPending && <Notice testId="claim-pending-notice">You have asked us to change your {Object.keys(member.pending_identity_changes).map(k => FIELD_LABELS[k].split(' (')[0].toLowerCase()).join(' and ')}. Our team will check this against your photo ID before it is updated on your card.</Notice>}
          <p className="text-base text-[hsl(var(--lux-ink))]">By submitting, you confirm that the information is true and that you hold the GS membership shown above. <strong>There is nothing to pay.</strong></p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Button variant="outline" className="h-12 text-base" onClick={() => setStep(4)}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
            <Button className="h-12 text-base bg-[hsl(var(--lux-royal))] text-white" disabled={busy || !readyToSubmit} onClick={submit} data-testid="claim-submit">{busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Submit for verification</Button>
          </div>
        </section>;
export const ClaimStep7 = ({
  member,
  s,
  setStep
}) => <section className="space-y-5 text-center py-4" data-testid="claim-step-7">
          <CreditCard className="w-14 h-14 mx-auto text-[hsl(var(--lux-royal))]" />
          <h2 className="text-2xl font-bold text-[hsl(var(--lux-royal))]" style={{
    fontFamily: 'Cormorant Garamond'
  }}>Your digital membership card is ready</h2>
          <p className="text-base text-[hsl(var(--lux-ink))]">Thank you for completing the move to our new digital membership system. Your card carries your photograph, GS membership number and your own unique QR code.</p>
          {member.card_url && <Link to={member.card_url}><Button className="h-12 text-base bg-[hsl(var(--lux-royal))] text-white" data-testid="claim-view-card">View my membership card</Button></Link>}
          {s.photo_id_status !== 'UPLOADED' && s.photo_id_status !== 'VERIFIED' || s.profile_photo_status !== 'UPLOADED' ? <div className="rounded-xl border border-[hsl(var(--lux-royal)/0.15)] bg-white p-5 text-left space-y-3 mt-4" data-testid="claim-add-docs">
              <div className="flex items-center gap-2 text-[hsl(var(--lux-royal))] font-semibold"><IdCard className="w-5 h-5" /> Optional — add your documents whenever convenient</div>
              <p className="text-base text-[hsl(var(--lux-ink))]">You chose to add {[s.photo_id_status !== 'UPLOADED' && s.photo_id_status !== 'VERIFIED' && 'your photo ID', s.profile_photo_status !== 'UPLOADED' && 'a photograph for your card'].filter(Boolean).join(' and ')} later. There is nothing to pay{s.profile_photo_status !== 'UPLOADED' ? ', and your card is re-issued with your photograph automatically' : ''}.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {s.photo_id_status !== 'UPLOADED' && s.photo_id_status !== 'VERIFIED' && <Button variant="outline" className="h-12 text-base" onClick={() => setStep(3)} data-testid="claim-add-id-later">Add photo ID</Button>}
                {s.profile_photo_status !== 'UPLOADED' && <Button variant="outline" className="h-12 text-base" onClick={() => setStep(4)} data-testid="claim-add-photo-later">Add photograph</Button>}
              </div>
            </div> : null}
          <div className="rounded-xl border border-[hsl(var(--lux-gold))] bg-white p-5 text-left space-y-3 mt-4" data-testid="claim-family-ask">
            <div className="flex items-center gap-2 text-[hsl(var(--lux-royal))] font-semibold"><Users className="w-5 h-5" /> Would you like to add family members to your GS Family Account?</div>
            <p className="text-base text-[hsl(var(--lux-ink))]">Children under 14 are free. Family members aged 14 and over join as members in their own right. Every person receives their own membership number, QR code and card.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <Link to="/membership/add-family"><Button variant="outline" className="w-full h-12 text-base" data-testid="claim-add-family">Add family member</Button></Link>
              <Link to="/"><Button variant="ghost" className="w-full h-12 text-base" data-testid="claim-no-family">No, continue</Button></Link>
            </div>
          </div>
        </section>;
export const ClaimConfirmation = ({
  busy,
  confirm,
  confirmError,
  confirmLabel,
  confirmValue,
  intro,
  isDob,
  isEmail,
  setConfirmError,
  setConfirmValue
}) => <Card className="border-0 shadow-xl rounded-2xl"><CardContent className="p-7 sm:p-9 space-y-5">
        {intro.test && <Notice tone="warn" testId="claim-test-notice"><p className="font-semibold">System test — synthetic record</p><p className="mt-1">{intro.test_notice}</p></Notice>}
        <h2 className="text-2xl font-bold text-[hsl(var(--lux-royal))]" style={{
      fontFamily: 'Cormorant Garamond'
    }} data-testid="claim-hello">{intro.name_missing ? 'Hello, welcome back' : `Hello ${intro.first_name}, welcome back`}</h2>
        {intro.recovery && <Notice tone="info" testId="claim-recovery-notice"><p>Our team has confirmed your existing membership and assigned your GS number. There is nothing to pay — please complete your details, photograph and photo ID so we can issue your digital card.</p></Notice>}
        <p className="text-base text-[hsl(var(--lux-ink))]">Our records already recognise your membership. <strong>There is nothing to pay</strong> — we simply need to move your details to the new digital system together.</p>
        <div className="rounded-xl border-2 border-[hsl(var(--lux-gold))] bg-white px-5 py-4 text-center">
          <div className="text-xs uppercase tracking-widest text-[hsl(var(--lux-ink)/0.6)]">Your GS Membership Number</div>
          <div className="text-xl sm:text-2xl font-bold tracking-wide sm:tracking-wider text-[hsl(var(--lux-royal))] mt-1 break-all" data-testid="claim-membership-number">{intro.membership_number}</div>
          {intro.membership_type && <div className="text-sm text-[hsl(var(--lux-ink)/0.7)] mt-1">{intro.membership_type} membership</div>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="claim-confirm" className="text-base">To keep your details safe, please confirm your {confirmLabel}</Label>
          <Input id="claim-confirm" data-testid="claim-confirm-input" type={isEmail ? 'email' : 'text'} className={`h-12 text-lg ${confirmError ? 'border-red-400' : ''}`} value={confirmValue} onChange={e => {
        setConfirmValue(isDob ? maskDob(e.target.value) : e.target.value);
        setConfirmError('');
      }} placeholder={isDob ? 'DD/MM/YYYY' : isEmail ? 'name@example.com' : 'e.g. NG3 1JE'} inputMode={isDob ? 'numeric' : isEmail ? 'email' : 'text'} autoComplete="off" autoCapitalize={isEmail ? 'none' : isDob ? 'off' : 'characters'} autoCorrect="off" spellCheck={false} />
          {isDob && !confirmError && <p className="text-sm text-[hsl(var(--lux-ink)/0.6)]">Type the digits only — for example 26021965 becomes 26/02/1965</p>}
          {confirmError && <p className="text-base text-red-800 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2" role="alert" data-testid="claim-confirm-error"><AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />{confirmError}</p>}
        </div>
        <Button className="w-full h-12 text-base bg-[hsl(var(--lux-royal))] text-white" disabled={busy || !confirmValue} onClick={confirm} data-testid="claim-confirm-btn">{busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Continue <ArrowRight className="w-4 h-4 ml-2" /></Button>
        <p className="text-sm text-[hsl(var(--lux-ink)/0.55)]">This secure link is personal to you and can be used once. Your information is stored privately and seen only by authorised administrators.</p>
      </CardContent></Card>;
export const ClaimStep2 = ({
  busy,
  detailsError,
  findAddress,
  form,
  guardian,
  guardianNeeded,
  isMissing,
  lookup,
  member,
  pickAddress,
  saveDetails,
  saveGuardian,
  setField,
  setForm,
  setGuardian,
  setStep
}) => <section className="space-y-5" data-testid="claim-step-2">
          <h2 className="text-2xl font-bold text-[hsl(var(--lux-royal))]" style={{
    fontFamily: 'Cormorant Garamond'
  }}>Step 2 — Complete or correct your information</h2>
          <ClaimField name="full_name" value={form.full_name || ''} missing={isMissing('full_name')} onChange={setField} hint="If your name has changed, our team will check it against your photo ID before updating your card." />
          <div className="grid sm:grid-cols-2 gap-4">
            <ClaimField name="email" value={form.email || ''} missing={isMissing('email')} onChange={setField} type="email" />
            <ClaimField name="phone" value={form.phone || ''} missing={isMissing('phone')} onChange={setField} type="tel" placeholder="07… or 0115…" />
          </div>
          <FamilySharedEmail checked={form.family_shared_email} required={member.family_shared_email_required} onCheckedChange={value => setField('family_shared_email', value)} />
          <div className="grid sm:grid-cols-2 gap-4">
            <ClaimField name="dob" value={form.dob || ''} missing={isMissing('dob')} onChange={setField} placeholder="DD/MM/YYYY" hint="For example 26/02/1965" />
            <div className="space-y-1.5">
              <Label className="text-base">{FIELD_LABELS.gender}{isMissing('gender') && <span className="ml-2 text-sm font-semibold text-amber-700" data-testid="claim-missing-gender">Required — please complete</span>}</Label>
              <Select value={form.gender || ''} onValueChange={v => setForm({
        ...form,
        gender: v
      })}>
                <SelectTrigger className={`h-12 text-base ${isMissing('gender') ? 'border-amber-400 bg-amber-50/40' : ''}`} data-testid="claim-field-gender"><SelectValue placeholder="Please choose" /></SelectTrigger>
                <SelectContent>{Object.entries(GENDER_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="rounded-xl border border-[hsl(var(--lux-royal)/0.12)] p-4 space-y-4 bg-white">
            <div className="flex items-center gap-2 text-[hsl(var(--lux-royal))] font-semibold"><MapPin className="w-5 h-5" /> Your home address</div>
            <div className="flex gap-2 items-end">
              <div className="flex-1"><ClaimField name="postcode" value={form.postcode || ''} missing={isMissing('postcode')} onChange={setField} placeholder="e.g. NG3 1JE" /></div>
              <Button type="button" variant="outline" className="h-12" onClick={() => findAddress()} disabled={lookup.loading} data-testid="claim-find-address">{lookup.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Find address'}</Button>
            </div>
            {lookup.error && <p className="text-sm text-[hsl(var(--lux-ink)/0.7)]" data-testid="claim-lookup-message">{lookup.error}</p>}
            {lookup.suggestions?.length > 0 && <div className="flex flex-wrap items-center gap-2" data-testid="claim-postcode-suggestions">
                <span className="text-sm text-[hsl(var(--lux-ink)/0.7)]">Did you mean:</span>
                {lookup.suggestions.map(sg => <Button key={sg} type="button" size="sm" variant="outline" className="h-9 font-mono" onClick={() => {
        setField('postcode', sg);
        findAddress(sg);
      }} data-testid={`claim-postcode-suggestion-${sg.replace(/\s+/g, '')}`}>{sg}</Button>)}
              </div>}
            {lookup.results.length > 0 && <div className="space-y-1.5">
                <Label className="text-base">Please choose your address</Label>
                <Select onValueChange={i => pickAddress(lookup.results[Number(i)])}>
                  <SelectTrigger className="h-12 text-base" data-testid="claim-address-select"><SelectValue placeholder={`${lookup.results.length} addresses found — choose yours`} /></SelectTrigger>
                  <SelectContent>{lookup.results.map((a, i) => <SelectItem key={stableRecordKey(a)} value={String(i)}>{[a.line_1, a.line_2, a.post_town].filter(Boolean).join(', ')}</SelectItem>)}</SelectContent>
                </Select>
              </div>}
            <ClaimField name="address_line_1" value={form.address_line_1 || ''} missing={isMissing('address_line_1')} onChange={setField} />
            <ClaimField name="address_line_2" value={form.address_line_2 || ''} missing={isMissing('address_line_2')} onChange={setField} />
            <div className="grid sm:grid-cols-2 gap-4"><ClaimField name="city" value={form.city || ''} missing={isMissing('city')} onChange={setField} /><ClaimField name="county" value={form.county || ''} missing={isMissing('county')} onChange={setField} /></div>
          </div>
          {guardianNeeded && <div className="rounded-xl border border-[hsl(var(--lux-gold))] p-4 space-y-3 bg-white" data-testid="claim-guardian-box">
              <div className="flex items-center gap-2 text-[hsl(var(--lux-royal))] font-semibold"><Users className="w-5 h-5" /> Parent or guardian</div>
              <p className="text-base text-[hsl(var(--lux-ink))]">This membership belongs to a child under 14, so we ask a parent or guardian to be linked to it. Please enter the parent or guardian's GS membership number.</p>
              {member.guardian ? <Notice tone="good" testId="claim-guardian-set">Linked to <strong>{member.guardian.full_name}</strong> ({member.guardian.membership_number}) — {member.guardian.status === 'LINKED' ? 'confirmed by our team.' : 'our team will confirm this shortly.'}</Notice> : <div className="grid sm:grid-cols-[1fr_180px_auto] gap-3 items-end">
                  <div className="space-y-1.5"><Label className="text-base">Parent / guardian GS number</Label><Input className="h-12 text-base" data-testid="claim-guardian-number" value={guardian.number} onChange={e => setGuardian({
          ...guardian,
          number: e.target.value
        })} /></div>
                  <div className="space-y-1.5"><Label className="text-base">Relationship</Label>
                    <Select value={guardian.relationship} onValueChange={v => setGuardian({
          ...guardian,
          relationship: v
        })}><SelectTrigger className="h-12 text-base" data-testid="claim-guardian-relationship"><SelectValue /></SelectTrigger>
                      <SelectContent>{['parent', 'guardian', 'grandparent', 'other'].map(v => <SelectItem key={v} value={v}>{v[0].toUpperCase() + v.slice(1)}</SelectItem>)}</SelectContent></Select></div>
                  <Button type="button" className="h-12 bg-[hsl(var(--lux-royal))] text-white" onClick={saveGuardian} disabled={busy || !guardian.number} data-testid="claim-guardian-save">Link</Button>
                </div>}
            </div>}
          {detailsError && <p className="text-base text-red-800 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2" role="alert" data-testid="claim-details-error"><AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />{detailsError}</p>}
          <div className="grid sm:grid-cols-2 gap-3">
            <Button variant="outline" className="h-12 text-base" onClick={() => setStep(1)}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
            <Button className="h-12 text-base bg-[hsl(var(--lux-royal))] text-white" disabled={busy} onClick={() => saveDetails(3)} data-testid="claim-step2-save">{busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Save and continue <ArrowRight className="w-4 h-4 ml-2" /></Button>
          </div>
        </section>;
